import React, { useState, useEffect } from "react";
import { useUserStore } from "../store/UserStore";
import { useBankAccountStore } from "../store/BankAccountStore";
import { ClipLoader } from "react-spinners";
import RequestNameChange from "./RequestNameChange";
import RequestAddressChange from "./RequestAddressChange";
import { base, edit_password, add_assist_bank_account, get_assist_bank_accounts, agent_banks } from "../constants/urls";
import { useAuth } from "../store/Auth";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import { FiPlus } from 'react-icons/fi';
import { BsBank } from 'react-icons/bs';
import { checkToken } from "./CheckToken";

export default function UserProfile() {
  const userDetails = useUserStore((state) => state.userDetails);
  const getAuth = useAuth((state) => state.auth);
  // const [showChangeOfName, setShowChangeOfName] = useState(false);
  // const [showChangeOfAddress, setShowChangeOfAddress] = useState(false);
  const [passcode, setPasscode] = useState("");
  const { password } = useParams();
  const [menu, setMenu]=useState("")
  const [banks, setBanks] = useState([]);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [newAccount, setNewAccount] = useState({
    bank_name: "",
    bank_code: "",
    account_number: "",
    account_name: "",
    sort_code: "",
    percentage: "100",
  });
  
  // Get bank account store methods
  const { 
    myBankAccounts = [], 
    loading: bankLoading, 
    error: bankError,
    setMyBankAccounts,
    setLoading,
    setError 
  } = useBankAccountStore();

  let data = {
    profile_type: userDetails.profileType,
    first_name: userDetails.firstName,
    middle_name: userDetails.middleName,
    last_name: userDetails.lastName,
    email: userDetails.email,
    phone: userDetails.phone,
    pulled_email: userDetails.pulledEmail,
    pulled_phone: userDetails.pulledPhone,
    pulled_address: userDetails.registeredAddress,
    current_state: userDetails.currentResidentialState,
    current_lg: userDetails.currentResidentialLGA,
  };

  const handleSubmit = async (e) => {
    let token = getAuth.token;
    e.preventDefault();
    try {
      fetch(base + edit_password + `${password}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(passcode),
      })
        .then((response) => {
          return response.json();
        })
        .then(({ data }) => {
          setPasscode(data);
        });
    } catch (error) {
      console.log(error);
      Swal.fire("unable to update password");
    }
  };

  // Fetch user's bank accounts
  const fetchMyBankAccounts = async () => {
    setLoading(true);
    let token = getAuth.token;

    try {
      const response = await fetch(`${base}${get_assist_bank_accounts}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.statusCode === 0) {
        setMyBankAccounts(data.data || []);
      } else {
        setError(data.message);
        setMyBankAccounts([]);
      }
    } catch (error) {
      setError("Failed to fetch bank accounts");
      setMyBankAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  // Add new bank account
  const addBankAccount = async (accountData) => {
    setLoading(true);
    let token = getAuth.token;
    try {
      const dataToSend = {
        ...accountData,
        percentage: accountData.percentage.toString()
      };

      const response = await fetch(`${base}${add_assist_bank_account}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });
      const data = await response.json();
      if (data.statusCode === 0) {
        await fetchMyBankAccounts(); // Refresh the list
        return { success: true };
      } else {
        setError(data.message);
        return { success: false, message: data.message };
      }
    } catch (error) {
      setError("Failed to add bank account");
      console.log(error);
      return { success: false, message: "Failed to add bank account" };
    } finally {
      setLoading(false);
    }
  };

  // Load accounts on component mount
  useEffect(() => {
    if (userDetails.accountTier === "1") {
      fetchMyBankAccounts();
    }
  }, [userDetails.accountTier]);

  // --- Auth & Security ---
  useEffect(() => {
    const verifySession = async () => {
      // WAIT for the token to exist before checking it
      if (getAuth?.authenticated && getAuth?.token) {
        const isValid = await checkToken(getAuth.token);
        if (!isValid) {
          localStorage.clear();
          window.location.replace("/login");
        }
      } else if (getAuth?.authenticated === false) {
        // Only boot them immediately if we are 100% sure they are logged out
        window.location.replace("/login");
      }
    };

    verifySession();
  }, [getAuth?.authenticated, getAuth?.token]);

  // Add this new function to fetch banks
  const fetchBanks = async () => {
    let token = getAuth.token;
    try {
      const response = await fetch(`${base}${agent_banks}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.statusCode === 0) {
        setBanks(data.data);
      } else {
        setError("Failed to fetch banks");
      }
    } catch (error) {
      setError("Failed to fetch banks");
    }
  };

  // Add this useEffect to fetch banks when component mounts
  useEffect(() => {
    fetchBanks();
  }, []);

  const handleAddAccount = async (e) => {
    e.preventDefault();

    try {
      const result = await Swal.fire({
        title: "Add Bank Account?",
        text: "Are you sure you want to add this account?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, add account",
      });

      if (result.isConfirmed) {
        const addResult = await addBankAccount(newAccount);
        if (addResult.success) {
          Swal.fire({
            title: "Success!",
            text: "Bank account added successfully",
            icon: "success",
            confirmButtonText: "Ok",
            confirmButtonColor: "#1C89DE",
          });
          setShowAddAccountModal(false);
          setNewAccount({
            bank_name: "",
            bank_code: "",
            account_number: "",
            account_name: "",
            sort_code: "",
            percentage: "100",
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: addResult.message,
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#1C89DE",
          });
        }
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to add bank account",
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#1C89DE",
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
            <h2 className="text-xl font-semibold text-white">Profile Information</h2>
          </div>
          
          <div className="p-6">
            {bankLoading ? (
              <div className="flex items-center justify-center py-12">
                <ClipLoader color={"#166434"} size={30} />
                <p className="ml-3 text-gray-500">Loading profile...</p>
              </div>
            ) : (
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={data?.first_name + " " + data?.last_name}
                    disabled
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={data?.phone}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={data?.email}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Residential Address
                  </label>
                  <input
                    type="text"
                    value={data?.current_state}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500"
                  />
                </div> */}
              </form>
            )}
          </div>
        </div>

        {userDetails.accountTier === "1" && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mt-8">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <BsBank className="text-white text-xl" />
                <h2 className="text-xl font-semibold text-white">Bank Account Information</h2>
              </div>
              {Array.isArray(myBankAccounts) && myBankAccounts.length === 0 && (
                <button
                  onClick={() => setShowAddAccountModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-150"
                >
                  <FiPlus className="mr-2" />
                  Add Account
                </button>
              )}
            </div>

            <div className="p-6">
              {bankLoading ? (
                <div className="flex justify-center items-center py-8">
                  <ClipLoader color={"#166434"} size={30} />
                  <p className="ml-3 text-gray-500">Loading bank accounts...</p>
                </div>
              ) : bankError ? (
                <div className="text-red-500 text-center py-4">{bankError}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Account Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Account Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Bank Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Sort Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Percentage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {Array.isArray(myBankAccounts) && myBankAccounts.length > 0 ? (
                      myBankAccounts.map((account) => (
                        <tr key={account.id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.account_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {account.account_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {account.bank_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {account.sort_code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                              {account.percentage}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr className="text-center text-gray-500 font-semibold">
                        <td colSpan="5" className="text-center text-gray-500 py-4">No bank accounts found</td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {showAddAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 transform transition-all">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Add Bank Account</h3>
                <button
                  onClick={() => setShowAddAccountModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  ×
                </button>
              </div>

              <form className="space-y-6" onSubmit={handleAddAccount}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank
                  </label>
                  <select
                    value={newAccount.bank_code}
                    onChange={(e) => {
                      const selectedBank = banks.find(bank => bank.code === e.target.value);
                      setNewAccount({
                        ...newAccount,
                        bank_code: e.target.value,
                        bank_name: selectedBank?.name || "",
                        sort_code: selectedBank?.code || ""
                      });
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Bank</option>
                    {banks.map((bank) => (
                      <option key={bank.code} value={bank.code}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={newAccount.account_name}
                    onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={newAccount.account_number}
                    onChange={(e) => setNewAccount({ ...newAccount, account_number: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Code
                  </label>
                  <input
                    type="text"
                    value={newAccount.sort_code}
                    disabled
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Percentage
                  </label>
                  <input
                    type="text"
                    disabled
                    value={`${newAccount.percentage}%`}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddAccountModal(false)}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
